import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MessageCircle, QrCode, CheckCircle2, Loader2, ArrowLeft, Keyboard, UserPlus, Send, Copy } from "lucide-react";

const LINE_GREEN = "#06C755";

export default function LineRegisterPage() {
  const [searchParams] = useSearchParams();
  const companyParam = (searchParams.get("company") || "").toUpperCase();
  const empParam = searchParams.get("emp") || searchParams.get("e") || "";

  const [step, setStep] = useState(empParam && companyParam ? "loading" : "scan"); // loading | scan | ready | done
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [identified, setIdentified] = useState(null);
  const [startData, setStartData] = useState(null);
  const [pendingCode, setPendingCode] = useState("");
  const [result, setResult] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCompany, setManualCompany] = useState(companyParam);
  const [manualEmp, setManualEmp] = useState("");

  const scannerRef = useRef(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { /* noop */ }
      try { await scannerRef.current.clear(); } catch (e) { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const parseQr = (text) => {
    let employee_id = text;
    let company_code = companyParam;
    if (text && text.startsWith("PRC:")) {
      const parts = text.split(":");
      employee_id = parts[1];
      if (parts[2]) company_code = parts[2];
    }
    return { employee_id, company_code };
  };

  // 本人特定 → そのまま登録を開始（1画面に集約）
  const identifyAndStart = useCallback(async ({ employee_id, company_code }) => {
    if (!company_code || !employee_id) {
      toast.error("QRコードを認識できませんでした");
      setStep("scan");
      return false;
    }
    setBusy(true);
    try {
      const idRes = await axios.post(`${API}/line/register/identify`, { company_code, employee_id });
      const ident = { ...idRes.data, company_code, employee_id };
      setIdentified(ident);
      const stRes = await axios.post(`${API}/line/register/start`, { company_code, employee_id });
      setStartData(stRes.data);
      setPendingCode(stRes.data.code);
      setStep("ready");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "登録を開始できませんでした");
      setStep("scan");
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const el = document.getElementById("line-qr-reader");
      if (!el) { setTimeout(startScanner, 300); return; }
      el.innerHTML = "";
      setScanning(true);
      const scanner = new Html5Qrcode("line-qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          if (navigator.vibrate) navigator.vibrate(80);
          await stopScanner();
          await identifyAndStart(parseQr(decodedText));
        },
        () => { /* scanning */ }
      );
    } catch (err) {
      setScanning(false);
      scannerRef.current = null;
      toast.error("カメラにアクセスできません。手入力をご利用ください。");
      setManualOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifyAndStart, stopScanner]);

  // 専用リンク(?emp=)なら自動で本人特定して開始
  useEffect(() => {
    if (step === "loading" && empParam && companyParam) {
      identifyAndStart({ employee_id: empParam, company_code: companyParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === "scan") startScanner();
    else stopScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // 完了をポーリング
  useEffect(() => {
    if (step !== "ready" || !pendingCode) return;
    let active = true;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/line/register/poll`, { params: { code: pendingCode } });
        if (!active) return;
        if (res.data.status === "done") {
          clearInterval(iv);
          setResult({ name: res.data.name || identified?.name });
          setStep("done");
        } else if (res.data.status === "expired") {
          clearInterval(iv);
          toast.error("コードの有効期限が切れました。もう一度お試しください。");
          if (identified) identifyAndStart(identified);
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => { active = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pendingCode]);

  const handleManual = async () => {
    if (!manualCompany.trim() || !manualEmp.trim()) {
      toast.error("企業コードと社員コードを入力してください");
      return;
    }
    setStep("loading");
    await identifyAndStart({ employee_id: manualEmp.trim(), company_code: manualCompany.trim().toUpperCase() });
  };

  const shareUrl = startData ? `https://line.me/R/share?text=${encodeURIComponent(startData.token)}` : "";

  const openAddFriend = () => { if (startData?.line_add_url) window.open(startData.line_add_url, "_blank"); };
  const openSendCode = () => { if (shareUrl) window.open(shareUrl, "_blank"); };

  const copyCode = () => {
    if (!startData) return;
    try { navigator.clipboard.writeText(startData.token); toast.success("コードをコピーしました"); } catch (e) { /* noop */ }
  };

  const resetAll = () => {
    setIdentified(null); setResult(null); setStartData(null);
    setPendingCode(""); setManualEmp(""); setStep("scan");
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center px-4 py-8" data-testid="line-register-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg" style={{ background: LINE_GREEN }}>
            <MessageCircle className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 className="text-lg font-bold leading-tight">LINE通知の登録</h1>
            <p className="text-xs text-slate-400">PRC勤怠</p>
          </div>
        </div>

        {/* LOADING */}
        {step === "loading" && (
          <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="line-register-loading">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: LINE_GREEN }} />
            <p className="text-sm text-slate-400">準備中...</p>
          </div>
        )}

        {/* SCAN */}
        {step === "scan" && (
          <div className="space-y-4" data-testid="line-register-scan">
            <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5">
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                <QrCode className="w-5 h-5" style={{ color: LINE_GREEN }} />
                <span>タイムカードのQRコードをかざしてください</span>
              </div>
              <div id="line-qr-reader" className="w-full aspect-square rounded-xl overflow-hidden bg-black/40 border border-slate-700" />
              {busy && (
                <div className="flex items-center justify-center gap-2 mt-3 text-sm" style={{ color: LINE_GREEN }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> 確認中...
                </div>
              )}
              {!scanning && !busy && <p className="text-xs text-slate-500 mt-3 text-center">カメラを起動しています…</p>}
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-4">
              <button type="button" onClick={() => setManualOpen((v) => !v)} className="flex items-center gap-2 text-sm text-slate-300 w-full" data-testid="line-register-manual-toggle">
                <Keyboard className="w-4 h-4 text-slate-400" /> QRが読めない場合は手入力
              </button>
              {manualOpen && (
                <div className="mt-3 space-y-3" data-testid="line-register-manual">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">企業コード</Label>
                    <Input value={manualCompany} onChange={(e) => setManualCompany(e.target.value)} placeholder="例: PRC001" className="bg-slate-700 border-slate-600 h-10" data-testid="line-register-manual-company" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">社員コード</Label>
                    <Input value={manualEmp} onChange={(e) => setManualEmp(e.target.value)} placeholder="例: 1001" className="bg-slate-700 border-slate-600 h-10" data-testid="line-register-manual-emp" />
                  </div>
                  <Button onClick={handleManual} disabled={busy} className="w-full" style={{ background: LINE_GREEN }} data-testid="line-register-manual-submit">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}次へ
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* READY: 2タップ（友だち追加 → コード送信） */}
        {step === "ready" && startData && (
          <div className="space-y-5" data-testid="line-register-ready">
            <div className="rounded-2xl border p-4 text-center" style={{ borderColor: `${LINE_GREEN}66`, background: `${LINE_GREEN}1a` }}>
              <p className="text-xs" style={{ color: `${LINE_GREEN}cc` }}>{identified?.company_name}</p>
              <p className="text-xl font-bold mt-0.5" data-testid="line-register-name">{identified?.name} さん</p>
              {identified?.has_line && <p className="text-xs text-amber-300 mt-1">LINEは登録済みです（再登録できます）</p>}
            </div>

            {!startData.receiver_configured ? (
              <div className="rounded-2xl border border-amber-600/40 bg-amber-600/10 p-5 text-amber-200 text-sm text-center" data-testid="line-register-notready">
                会社のLINEがまだ接続されていません。管理者にお知らせください。
              </div>
            ) : (
              <>
                {/* STEP 1 */}
                <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white" style={{ background: LINE_GREEN }}>1</span>
                    <p className="text-sm text-slate-200 font-medium">{startData.receiver_name ? `「${startData.receiver_name}」を友だち追加` : "会社の公式アカウントを友だち追加"}</p>
                  </div>
                  <Button onClick={openAddFriend} disabled={!startData.line_add_url} className="w-full h-12" style={{ background: LINE_GREEN }} data-testid="line-register-addfriend-btn">
                    <UserPlus className="w-5 h-5 mr-2" />友だち追加
                  </Button>
                  {!startData.line_add_url && <p className="text-xs text-slate-500 text-center">友だち追加リンクが未設定です。管理画面のQRで追加してください。</p>}
                </div>

                {/* STEP 2 */}
                <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white" style={{ background: LINE_GREEN }}>2</span>
                    <p className="text-sm text-slate-200 font-medium">下のボタンでコードを送信（入力不要）</p>
                  </div>
                  <div className="flex items-center justify-center gap-2 py-1">
                    <span className="text-2xl font-mono font-bold tracking-widest" data-testid="line-register-code">{startData.token}</span>
                    <button type="button" onClick={copyCode} className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-700" data-testid="line-register-copy"><Copy className="w-4 h-4 text-slate-300" /></button>
                  </div>
                  <Button onClick={openSendCode} className="w-full h-12" style={{ background: LINE_GREEN }} data-testid="line-register-send-btn">
                    <Send className="w-5 h-5 mr-2" />このコードをLINEで送信
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-2 text-sm" style={{ color: LINE_GREEN }} data-testid="line-register-waiting">
                  <Loader2 className="w-4 h-4 animate-spin" /> 送信をお待ちしています…
                </div>
              </>
            )}

            <button type="button" onClick={resetAll} className="flex items-center justify-center gap-1.5 text-sm text-slate-500 w-full pt-1" data-testid="line-register-rescan">
              <ArrowLeft className="w-4 h-4" /> 最初からやり直す
            </button>
          </div>
        )}

        {/* DONE */}
        {step === "done" && result && (
          <div className="space-y-6 text-center" data-testid="line-register-success">
            <div className="rounded-2xl border p-8" style={{ borderColor: `${LINE_GREEN}66`, background: `${LINE_GREEN}1a` }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: LINE_GREEN }}>
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <p className="text-xl font-bold">登録が完了しました</p>
              <p className="text-sm text-slate-300 mt-2">{result.name}</p>
              <p className="text-sm text-slate-400 mt-1">打刻・勤怠のお知らせがLINEに届きます</p>
            </div>
            <Button onClick={resetAll} variant="outline" className="w-full border-slate-600 text-slate-200 hover:bg-slate-800" data-testid="line-register-again-btn">
              別の人を登録する
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
